import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { StateService } from '@uirouter/core';
import { AddonsUtil } from 'my-account/activity/line-detail/addon/services/addons-util';
import { AddonLines, AddOnPassOption } from 'my-account/interface';
import { Cloudinary } from 'services/cloudinary';
import { LineSummary } from 'store/account-summary/models/line-summary';
import { AddonPassStatus, AddonType, ProgressStatus } from 'my-account/constants';

type SummaryTableData = {
    displayName: string;
    gtpStatus: string;
    mexCanStatus: string;
};

@Component({
    selector: 'xm-addon-summary-table',
    templateUrl: './addon-summary-table.html',
    styleUrls: ['./addon-summary-table.scss'],
    standalone: false
})

export class XmAddonSummaryTable implements OnInit, OnChanges {
    @Input() public addonLines: AddonLines = {};
    @Input() public lines: LineSummary[];
    @Input() public pageData: CmsAddonSummaryData;
    @Input() public isGTP: boolean = false;
    @Input() public isMexCan: boolean = false;
    @Input() public isSuccess: boolean = false;
    @Input() public overallStatus: string;

    public gtpLines: AddOnPassOption[] = [];
    public mexCanLines: AddOnPassOption[] = [];
    public tableData: SummaryTableData[] = [];
    public intent: string;
    public gtpIcon: MediaImageOptions;
    public mexCanIcon: MediaImageOptions;

    private state: StateService;
    private addonsUtil: AddonsUtil;


    constructor(state: StateService, addonsUtil: AddonsUtil) {
        Object.assign(this, { state, addonsUtil });
    }


    public ngOnInit(): void {
        this.intent = this.state.params.addonAction;
        this.gtpLines = this.addonLines.gtp?.lines || [];
        this.mexCanLines = this.addonLines.can_mex?.lines || [];

        this.gtpIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.gtpIcon)
        };

        this.mexCanIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.mexcanIcon)
        };

        this.buildData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.overallStatus && changes?.overallStatus.currentValue === ProgressStatus.SUCCESS) {
            this.isSuccess = changes?.overallStatus.currentValue === ProgressStatus.SUCCESS;
            this.buildData();
        }
    }

    /**
     * Builds and returns an array of summary table data objects by mapping over the available line summaries.
     * For each line, it finds the corresponding GTP and MEX_CAN_PASS add-on options, determines their display statuses,
     * and constructs a summary object containing the display name and statuses.
     *
     * @returns {SummaryTableData[]} An array of summary table data objects, each containing the display name,
     * GTP status, and MEX_CAN_PASS status for a line.
     */
    public buildData(): SummaryTableData[] {
        this.tableData = this.lines.map((line: LineSummary) => {
            const gtpLine: AddOnPassOption | undefined = this.gtpLines.find((gtp: AddOnPassOption) => gtp.line_id === line.id.key);
            const mexCanLine: AddOnPassOption | undefined = this.mexCanLines.find((mexCan: AddOnPassOption) => mexCan.line_id === line.id.key);
            const gtpStatus: string = gtpLine ? this.getAddonPassDisplayStatus(gtpLine, AddonType.GTP) : '—';
            const mexCanStatus: string = mexCanLine ? this.getAddonPassDisplayStatus(mexCanLine, AddonType.CAN_MEX_UNL_PASS) : '—';

            return {
                displayName: line.displayName,
                gtpStatus: gtpStatus,
                mexCanStatus: mexCanStatus
            };
        });

        return this.tableData;
    }

    /**
     * Returns the display status for an AddOnPassOption based on pending actions or its current status.
     */
    public getAddonPassDisplayStatus(addon: AddOnPassOption, addonType: string): string {

        if (this.addonsUtil?.postAddOnsPayload?.addOnType === addonType) {
            if (this.addonsUtil?.postAddOnsPayload?.lineIdKeys?.includes(addon.line_id)) {
                if (this.addonsUtil.postAddOnsPayload.action === 'ADD') {
                    return this.isSuccess ? AddonPassStatus.ADDED : AddonPassStatus.ADD_PENDING;
                } else if (this.addonsUtil.postAddOnsPayload.action === 'REMOVE') {
                    return this.isSuccess ? AddonPassStatus.REMOVED : AddonPassStatus.REMOVE_PENDING;
                }
            }
        }

        // For lines that don't match, return the mapped status from AddonPassStatus enum
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return AddonPassStatus[addon.status as keyof typeof AddonPassStatus] || addon.status;
    }
}
